import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { confirmMatchingFormFields } from 'src/app/shared/validators'
import { minPasswordLength } from 'src/utils/aws-utils'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AuthService } from '../../services/auth.service'

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AuthComponent implements OnInit {
  currentViewLogin = true

  private authToken: string
  allowRegistration = false
  errorText = ''
  doWorkSubscription: Subscription = null
  loginSubscription: Subscription = null

  loginFormModel = this.formBuilder.group({
    username: ['', [Validators.required, Validators.email]],
    // Allow users to submit passwords set before the minimum length was increased to 12
    password: ['', [Validators.required, Validators.minLength(8)]],
  })

  registerFormModel = this.formBuilder.group(
    {
      username: ['', [Validators.required, Validators.email]],
      displayName: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(minPasswordLength)]],
      confirmPassword: ['', [Validators.required]],
      agreeToTerms: [false, [Validators.required, Validators.requiredTrue]],
    },
    {
      validators: confirmMatchingFormFields('password', 'confirmPassword'),
    }
  )

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    public uiService: UiService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.authToken = this.route.snapshot.queryParams['authToken']
    if (!this.authToken) {
      // if users get here directly without an authToken, we redirect them to the root,
      // which should redirect them back here with an authToken.
      this.router.navigateByUrl('/')
    } else {
      let register: string = this.route.snapshot.queryParams['register']
      if (!!register && register.toLowerCase() === 'true') {
        this.currentViewLogin = false
        this.allowRegistration = true
      }
      if (!!this.providedEmail) {
        this.registerFormModel.patchValue({ username: this.providedEmail })
        this.registerFormModel.get('username').disable()
      }
    }
  }

  get productName() {
    return this.uiService.productName
  }

  toggleView() {
    this.errorText = ''
    this.currentViewLogin = !this.currentViewLogin
  }

  loginButtonClick() {
    this.errorText = ''
    let { username, password } = this.loginFormModel.value
    this.loginSubscription = this.apiService.login(
      username,
      password,
      this.authToken,
      (returnUrl) => {
        // successful login.  Need to navigate to returnUrl
        this.loginSubscription = null
        window.location.href = returnUrl
      },
      () => {
        // unsuccessful login.  Display error to user.
        this.loginSubscription = null
        this.errorText = 'Incorrect email or password'
      },
      (error) => {
        this.loginSubscription = null
        // this next line may be the only place in the application where we don't pass
        // window.location.href as the first argument.  That's because, if the user's auth
        // token has expired, they'll get a 401 when submitting claims.  handleArpioError
        // handles a 401 by walking the user through an auth flow and landing them
        // back on the URL passed as the first argument.  But in this case, that URL
        // is the login page with the expired auth token.  It creates an infinite loop.
        // We'll pass '/' instead.
        handleArpioError(this.uiService.buildRootUrl(), this.apiService, this.uiService)(error)
      }
    )
  }

  get loginButtonDisabled() {
    return !this.loginFormModel.valid || this.submitting
  }

  registerButtonClick() {
    this.errorText = ''
    let { username, displayName, password } = this.registerFormModel.getRawValue()
    this.loginSubscription = this.apiService.registerUser(
      username,
      displayName,
      password,
      this.authToken,
      (returnUrl) => {
        window.location.href = returnUrl
      },
      () => {
        this.errorText = `User ${username} already exists`
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  get registerButtonDisabled() {
    return !this.registerFormModel.valid || this.submitting
  }

  get providedEmail(): string {
    return this.route.snapshot.queryParamMap.get('inviteeEmail')
  }

  get passwordError(): string {
    let passwordLength = this.registerFormModel.value['password'].length
    if (passwordLength > 2 && passwordLength < minPasswordLength) {
      return 'Too short'
    }
    return null
  }

  get confirmPasswordError(): string {
    let confirmPasswordLength = this.registerFormModel.value['confirmPassword'].length
    if (
      confirmPasswordLength > 5 &&
      this.registerFormModel.value['password'] !== this.registerFormModel.value['confirmPassword']
    ) {
      return "Doesn't match"
    }
    return null
  }

  get submitting() {
    return subscriptionActive(this.loginSubscription)
  }
}
